/*
 * Copyright (c) Funtip GbR 2018.
 * Simon Oldeboershuis, Perry Prust
 */

import { faFileImport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import { PagingComponent } from '../../../reactutils/withPagination';
import { SearchComponent } from '../../../reactutils/withSearch';
import { withTitle } from '../../../reactutils/withTitle';
import { PlayListEntry } from '../../../services/playlist';
import { ButtonWithConfirmation } from '../../common/ButtonWithConfirmation';
import { LoadingIndicatorContent } from '../../common/LoadingIndicatorSpinner';
import { AlbumLink } from '../AlbumLink';
import { SongLink } from '../SongLink';
import {
    playlistBackward,
    playlistFastBackward,
    playlistFastForward,
    playlistForward,
    playlistImportMusicbrainzSong,
    playlistInitiateSearch,
    searchPlaylist
} from './actions';
import PlaylistSearchTimeFrame from './PlaylistSearchTimeFrame';
import {
    playlistSearchCount,
    playlistSearchCurrentPos,
    playlistSearchIsLoading,
    playlistSearchList,
    playlistSearchPageSize,
    playlistSearchString
} from './selectors';

const PlaylistSearch: React.FC<{}> = () => {
    const dispatch = useDispatch();

    const selectedEntities = useSelector(playlistSearchList);

    React.useEffect(() => {
        dispatch(searchPlaylist());
    }, [dispatch]);

    const onSearch = (s: string) => playlistInitiateSearch(s);

    const onForward = () => playlistForward();

    const onFastForward = () => playlistFastForward();
    const onBackward = () => playlistBackward();

    const onFastBackward = () => playlistFastBackward();
    const isLoading = useSelector(playlistSearchIsLoading);

    const showPlaylistEntry = (entity: PlayListEntry) => {
        return (
            <tr key={entity.id} className="d-flex">
                <td className="col-2">
                    <LoadingIndicatorContent isLoading={isLoading}>{entity.songArtist}</LoadingIndicatorContent>
                </td>
                <td className="col-3">
                    <LoadingIndicatorContent isLoading={isLoading}>
                        <SongLink songId={entity.songId}>{entity.songName}</SongLink>
                    </LoadingIndicatorContent>
                </td>
                <td className="col-3">
                    <LoadingIndicatorContent isLoading={isLoading}>
                        <AlbumLink albumId={entity.albumId} withImage={true} />
                    </LoadingIndicatorContent>
                </td>
                <td className="col-2">
                    <LoadingIndicatorContent isLoading={isLoading}>
                        {moment
                            .utc(entity.utcDate)
                            .local()
                            .format('DD.MM.YYYY HH:mm')}
                    </LoadingIndicatorContent>
                </td>
                <td className="col-2">
                    <LoadingIndicatorContent isLoading={isLoading}>
                        <ButtonWithConfirmation
                            title="Song importieren"
                            message="Sind Sie sicher, dass Sie den Song von Musicbrainz importieren wollen"
                            onOK={() => dispatch(playlistImportMusicbrainzSong(entity.songId))}
                        >
                            <FontAwesomeIcon icon={faFileImport} />
                        </ButtonWithConfirmation>
                    </LoadingIndicatorContent>
                </td>
            </tr>
        );
    };
    return (
        <>
            <PlaylistSearchTimeFrame />
            <div className="d-flex justify-content-between my-2 flex-wrap">
                <SearchComponent
                    onSearch={onSearch}
                    searchResultCountSelector={playlistSearchCount}
                    searchStringSelector={playlistSearchString}
                />
                <PagingComponent
                    onBackward={onBackward}
                    onFastBackward={onFastBackward}
                    onFastForward={onFastForward}
                    onForward={onForward}
                    searchResultCount={playlistSearchCount}
                    searchResultCurrentPos={playlistSearchCurrentPos}
                    searchResultPageSize={playlistSearchPageSize}
                />
            </div>
            <Table striped={true}>
                <thead>
                    <tr className="d-flex">
                        <th className="col-2">Artist</th>
                        <th className="col-3">Song</th>
                        <th className="col-3">Album</th>
                        <th className="col-2">Spielzeit</th>
                        <th className="col-2">Import</th>
                    </tr>
                </thead>
                <tbody>{selectedEntities && selectedEntities.map(e => showPlaylistEntry(e))}</tbody>
            </Table>
        </>
    );
};

export default R.compose(withTitle('Playlist'))(PlaylistSearch);

export const playlistSearchPath = () => '/playlist-admin/search';